import styled from '@emotion/styled';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Route,
    Routes,
    useNavigate,
    useParams,
    useLocation
} from 'react-router-dom';
import {
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react';
import { grey } from '@mui/material/colors';
import { staticColors } from '@/lib/theme';
import { TextMedium } from '@/typography/Typography';
import { TabErrorBoundaryWrapper } from './TabErrorBoundary';

const SwitchSelect = styled.div`
    margin-top: ${(props: any) => props.theme.spacing(2)};
    display: flex;
    gap: 10px;
    position: relative;
    overflow: visible;
    align-items: flex-end;
    /* Must be 100% so the container width is always driven by the parent,
       never by the flex children. Without this, inside a MUI Grid (flex)
       parent the container can grow to fit the More button's text when
       isOverflowSelected=true, which feeds back into ResizeObserver and
       causes the tab count to oscillate. */
    width: 100%;
    min-width: 0;
`;

interface BadgeProps {
    selected?: boolean;
}

const Badge = styled.div<BadgeProps>`
    background: ${(props: any) =>
        props.selected ? props.theme.palette.primary.main : grey[100]};
    color: ${(props) => (props.selected ? 'white' : grey[700])};
    border-radius: 16px;
    padding: 0px 8px;
    height: 22px;
    /* width: 28px; */
    margin-left: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

export interface SwitchItem {
    primaryText: string;
    badgeText: string;
    route: string;
    search?: string;
    path?: string;
    overrideTestPath?: string;
    routeComponent: ReactNode;
}

interface ViewSwitchProps {
    itemList: SwitchItem[];
    setSelectedIndex: (index: number | null) => void;
    selectedIndex: number | null;
}

const MORE_BUTTON_APPROX_WIDTH = 100; // px – approximate width of the "More ▾" button

/** Right margin of PageLayout's <main>, the only gutter to the right of the tabs. */
const CONTENT_RIGHT_GUTTER = 20;

export const ViewSwitch = ({
    itemList,
    setSelectedIndex,
    selectedIndex
}: ViewSwitchProps) => {
    // ── overflow state ────────────────────────────────────────────────────────
    const [visibleCount, setVisibleCount] = useState<number>(
        itemList?.length ?? 0
    );
    const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    /** Refs to ghost (hidden measurement) buttons, one per item */
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const calculateVisibleTabs = useCallback(() => {
        if (!containerRef.current || !itemList?.length) return;

        // offsetWidth alone cannot be trusted: `.App` shrink-wraps its content
        // (min-width: fit-content), so a row that does not fit widens every ancestor
        // and this container then measures wide enough to hold the very row that
        // inflated it — "they all fit" stays true and the row never collapses. The
        // row's left edge in *page* coordinates is immune to that inflation, so take
        // whichever of the two budgets is smaller. scrollX is what makes the left
        // edge scroll-invariant: read straight off getBoundingClientRect it shrinks
        // as the document scrolls right, handing back exactly the inflated budget
        // this clamp exists to reject. The row is assumed to sit in normal flow with
        // no ancestor transform — inside a transformed or portalled container the
        // offsetWidth term is the one that must win.
        const rowLeftInPage =
            containerRef.current.getBoundingClientRect().left + window.scrollX;
        const availableWidth = Math.max(
            0,
            document.documentElement.clientWidth -
                rowLeftInPage -
                CONTENT_RIGHT_GUTTER
        );
        const containerWidth = Math.min(
            containerRef.current.offsetWidth,
            availableWidth
        );
        const widths = tabRefs.current
            .slice(0, itemList.length)
            .map((t) => t?.offsetWidth ?? 0);

        // If measurement isn't ready yet, bail and wait for layout
        if (widths.some((w) => w === 0)) return;

        // Check whether all tabs fit with no overflow dropdown
        // Total width = Σwidths + (n-1)×gap
        const totalAllWidth =
            widths.reduce((acc, w) => acc + w, 0) +
            Math.max(0, widths.length - 1) * 10;

        if (totalAllWidth <= containerWidth) {
            setVisibleCount(itemList.length);
            return;
        }

        // Find how many tabs fit alongside the "More" button
        // Width needed for i+1 visible tabs: Σwidths[0..i] + i×gap + gap + More
        let totalTabWidths = 0;
        let count = 0;
        for (let i = 0; i < widths.length; i++) {
            const newTabWidths = totalTabWidths + widths[i];
            const gapsBetween = i * 10;
            const gapBeforeMore = 10;
            if (
                newTabWidths +
                    gapsBetween +
                    gapBeforeMore +
                    MORE_BUTTON_APPROX_WIDTH >
                containerWidth
            ) {
                break;
            }
            totalTabWidths = newTabWidths;
            count++;
        }
        setVisibleCount(Math.max(count, 1));
    }, [itemList.length]);

    // Measure after DOM paint
    useLayoutEffect(() => {
        calculateVisibleTabs();
    }, [calculateVisibleTabs]);

    // Re-measure on container resize
    // RAF debounce prevents ResizeObserver oscillation at boundary widths:
    // when visibleCount changes, the DOM layout shifts slightly, which would
    // re-trigger the observer synchronously — causing a shake loop.
    useEffect(() => {
        let rafId: number;
        const debouncedCalculate = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(calculateVisibleTabs);
        };
        const observer = new ResizeObserver(debouncedCalculate);
        if (containerRef.current) observer.observe(containerRef.current);
        // The container's own width can stay put while the viewport changes (it is
        // content-driven whenever the row overflows), so the observer alone would
        // miss a resize that changes how many tabs actually fit.
        window.addEventListener('resize', debouncedCalculate);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', debouncedCalculate);
            cancelAnimationFrame(rafId);
        };
    }, [calculateVisibleTabs]);
    // ─────────────────────────────────────────────────────────────────────────

    const selectedStyle = (index: number) =>
        index == selectedIndex
            ? {
                  zIndex: 10
              }
            : {
                  color: 'rgba(0, 0, 0, 0.54)',
                  borderBottom: '0px'
              };

    const navigate = useNavigate();
    const location = useLocation();

    const params = useParams();

    // Helper function to find the matching tab index for current route
    const findMatchingTabIndex = (pathname: string): number => {
        let matchedIndex = 0;

        itemList?.forEach((el, index) => {
            const regex = new RegExp(el.route);
            let isMatch = regex.test(pathname);

            if (el.overrideTestPath) {
                isMatch = pathname.indexOf(el.overrideTestPath) > -1;
            }

            if (isMatch) {
                matchedIndex = index;
            }
        });

        return matchedIndex;
    };

    // Effect to sync selectedIndex with current route
    useEffect(() => {
        if (itemList && itemList.length > 0) {
            const matchedIndex = findMatchingTabIndex(location.pathname);

            // Only update if different to prevent unnecessary re-renders
            if (matchedIndex !== selectedIndex) {
                setSelectedIndex(matchedIndex);
            }
        }
    }, [location.pathname, itemList.length, selectedIndex, setSelectedIndex]);

    // Effect to handle initial navigation to first tab
    useEffect(() => {
        if (itemList && itemList.length > 0) {
            // If we're at the parent route with no sub-route, navigate to first tab
            if ('' == params['*'] || params['*'] == undefined) {
                const pathSegments = location.pathname
                    .split('/')
                    .filter((segment) => segment !== '');
                const parentPath = pathSegments.join('/');
                const firstRoute = itemList[0]?.route;
                if (firstRoute) {
                    const searchString = location.search;
                    const targetPath = `/${parentPath}/${firstRoute}${searchString}`;
                    // Only navigate if not already at the target
                    if (location.pathname !== targetPath.split('?')[0]) {
                        navigate(targetPath, { replace: true });
                    }
                }
            }
        }
    }, [itemList.length, params, location.pathname, navigate]);

    const changeView = (index: number) => {
        const selectedItem = itemList[index];

        // Don't update selectedIndex here - let the route sync effect handle it
        // This prevents the visual flash from double updates

        // Find the common parent path for all tabs
        const pathSegments = location.pathname
            .split('/')
            .filter((segment) => segment !== '');

        let parentPathSegments: string[] = [];

        // Determine the parent path based on current location
        if (pathSegments.length === 1) {
            // We're at a top-level route like /settings
            // Parent is the current segment itself
            parentPathSegments = pathSegments;
        } else if (pathSegments.includes('page')) {
            // Handle pagination: /users/userId/user-transactions/page/2
            // Find the route pattern and extract everything before it
            const pageIndex = pathSegments.indexOf('page');
            // Get the route base (e.g., 'user-transactions' from 'user-transactions/page/0')
            const selectedRouteBase = selectedItem.route.split('/')[0];

            // Find where the current route starts in the path
            let routeStartIndex = -1;
            for (let i = 0; i < pageIndex; i++) {
                if (
                    pathSegments[i] === selectedRouteBase ||
                    pathSegments
                        .slice(i)
                        .join('/')
                        .startsWith(selectedRouteBase)
                ) {
                    routeStartIndex = i;
                    break;
                }
            }

            if (routeStartIndex > 0) {
                // Everything before the route is the parent
                parentPathSegments = pathSegments.slice(0, routeStartIndex);
            } else {
                // Fallback: everything before 'page' minus one segment
                parentPathSegments = pathSegments.slice(0, pageIndex - 1);
            }
        } else {
            // Regular nested route: /users/userId/tab-name
            // Check if the last segment is one of our tab routes
            const lastSegment = pathSegments[pathSegments.length - 1];
            const routeBases = itemList.map((item) => item.route.split('/')[0]);

            // If the last segment matches a route base, remove it to get parent
            if (routeBases.includes(lastSegment)) {
                parentPathSegments = pathSegments.slice(0, -1);
            } else {
                // Check if any route base is in the path (for multi-segment routes)
                let foundRouteIndex = -1;
                for (let i = pathSegments.length - 1; i >= 0; i--) {
                    if (routeBases.includes(pathSegments[i])) {
                        foundRouteIndex = i;
                        break;
                    }
                }

                if (foundRouteIndex > 0) {
                    parentPathSegments = pathSegments.slice(0, foundRouteIndex);
                } else {
                    // No tab route found in path - we're at the base route
                    // Keep all segments as the parent (e.g., /users/{userId})
                    parentPathSegments = pathSegments;
                }
            }
        }

        const parentPath = parentPathSegments.join('/');

        // Only navigate if we're actually changing routes
        // Preserve search params when navigating
        const searchString = location.search;
        const targetPath = `/${parentPath}/${selectedItem?.route}${searchString}`;
        if (location.pathname !== targetPath.split('?')[0]) {
            navigate(targetPath, { replace: true });
        }
    };

    const getBadgeText = (el: SwitchItem, selected: boolean) => {
        let badgeText = el.badgeText;
        if (+el.badgeText && parseInt(el.badgeText) > 100) {
            badgeText = '99+';
        }
        return <Badge selected={selected}>{badgeText}</Badge>;
    };

    const overflowItems = itemList?.slice(visibleCount) ?? [];
    const isOverflowSelected =
        selectedIndex !== null &&
        selectedIndex !== undefined &&
        selectedIndex >= visibleCount;

    const tabButtonStyle = (index: number) => ({
        border: 'none',
        borderBottom: '2px solid',
        borderRadius: '10px 10px 0px 0px',
        textTransform: 'capitalize' as const,
        whiteSpace: 'nowrap' as const,
        ...selectedStyle(index)
    });

    return (
        <>
            {itemList && (
                <>
                    <SwitchSelect ref={containerRef}>
                        {/* ── Ghost measurement container ──────────────────────
                            Invisible, out of flow. Used only to measure tab widths
                            so ResizeObserver can calculate how many fit. */}
                        <div
                            aria-hidden="true"
                            style={{
                                position: 'fixed',
                                top: '-9999px',
                                left: '-9999px',
                                display: 'flex',
                                gap: '10px',
                                visibility: 'hidden',
                                pointerEvents: 'none',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {itemList.map((el, index) => (
                                <Button
                                    ref={(btn) => {
                                        tabRefs.current[index] = btn;
                                    }}
                                    variant="outlined"
                                    key={`measure-${index}`}
                                    tabIndex={-1}
                                    style={{
                                        border: 'none',
                                        borderBottom: '2px solid',
                                        borderRadius: '10px 10px 0px 0px',
                                        textTransform: 'capitalize',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <TextMedium>{el.primaryText}</TextMedium>
                                    {el.badgeText && (
                                        <Badge>{el.badgeText}</Badge>
                                    )}
                                </Button>
                            ))}
                        </div>

                        {/* ── Visible tabs ──────────────────────────────────── */}
                        {itemList.slice(0, visibleCount).map((el, index) => (
                            <Button
                                variant="outlined"
                                key={index}
                                onClick={() => changeView(index)}
                                style={{
                                    ...tabButtonStyle(index),
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <TextMedium>{el.primaryText}</TextMedium>
                                {el.badgeText && (
                                    <Badge selected={index === selectedIndex}>
                                        {el.badgeText}
                                    </Badge>
                                )}
                            </Button>
                        ))}

                        {/* ── "More" overflow button ────────────────────────── */}
                        {overflowItems.length > 0 && (
                            <Button
                                variant="outlined"
                                onClick={(e) => setMoreAnchor(e.currentTarget)}
                                endIcon={
                                    <ExpandMoreIcon
                                        fontSize="small"
                                        style={{
                                            transition: 'transform 0.2s',
                                            transform: moreAnchor
                                                ? 'rotate(180deg)'
                                                : 'none'
                                        }}
                                    />
                                }
                                style={{
                                    border: 'none',
                                    borderBottom: isOverflowSelected
                                        ? '2px solid'
                                        : '0px',
                                    borderRadius: '10px 10px 0px 0px',
                                    textTransform: 'capitalize',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    zIndex: isOverflowSelected ? 10 : undefined,
                                    color: isOverflowSelected
                                        ? undefined
                                        : 'rgba(0, 0, 0, 0.54)'
                                }}
                            >
                                <TextMedium>More</TextMedium>
                            </Button>
                        )}

                        {/* ── Bottom border line ────────────────────────────── */}
                        <div
                            style={{
                                height: '0.5px',
                                width: '100%',
                                background: staticColors.buttonGreyText,
                                opacity: 0.2,
                                borderRadius: '2px',
                                position: 'absolute',
                                right: 0,
                                zIndex: 9,
                                bottom: '1px'
                            }}
                        />
                    </SwitchSelect>

                    {/* ── Overflow menu ─────────────────────────────────────── */}
                    <Menu
                        anchorEl={moreAnchor}
                        open={Boolean(moreAnchor)}
                        onClose={() => setMoreAnchor(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                    >
                        {overflowItems.map((el, i) => {
                            const actualIndex = visibleCount + i;
                            return (
                                <MenuItem
                                    key={actualIndex}
                                    selected={actualIndex === selectedIndex}
                                    onClick={() => {
                                        changeView(actualIndex);
                                        setMoreAnchor(null);
                                    }}
                                >
                                    <TextMedium>{el.primaryText}</TextMedium>
                                    {el.badgeText &&
                                        getBadgeText(
                                            el,
                                            actualIndex === selectedIndex
                                        )}
                                </MenuItem>
                            );
                        })}
                    </Menu>

                    <Box m={3}></Box>
                    <Routes>
                        {itemList.map((el, index) => (
                            <Route
                                key={index}
                                path={el?.path ?? el?.route}
                                element={
                                    <TabErrorBoundaryWrapper
                                        tabName={el.primaryText}
                                    >
                                        {el?.routeComponent}
                                    </TabErrorBoundaryWrapper>
                                }
                            />
                        ))}
                    </Routes>
                </>
            )}
        </>
    );
};
